import { useEffect, useState } from "react";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { rs, todayIso, monthOf, fmtMonth, daysInMonth } from "@/lib/format";
import { PageHeader, Panel } from "@/components/Kpi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function FixedCosts() {
  const [month, setMonth] = useState(monthOf(todayIso()));
  const [heads, setHeads] = useState([]);
  const [costs, setCosts] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/expense-heads").then((r) => setHeads(r.data.filter((h) => h.kind === "fixed"))); }, []);
  useEffect(() => { setCosts(null); api.get(`/fixed-costs/${month}`).then((r) => setCosts(r.data.costs)); }, [month]);

  const total = costs ? Object.values(costs).reduce((a, b) => a + (Number(b) || 0), 0) : 0;
  const save = async () => {
    setSaving(true);
    try { await api.put(`/fixed-costs/${month}`, { costs }); toast.success(`Fixed costs saved for ${fmtMonth(month)}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Fixed Costs" subtitle="One amount per head for the whole month. Reports pro-rate these for 15-day or weekly ranges.">
        <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-44 bg-white" data-testid="fixed-month-picker" />
        <Button onClick={save} disabled={!costs || saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-fixed-costs-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}Save
        </Button>
      </PageHeader>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel title={fmtMonth(month)} className="lg:col-span-2" testId="fixed-costs-panel">
          {!costs ? <div className="text-slate-500">Loading…</div> : (
            <div className="divide-y divide-slate-100">
              {heads.map((h) => (
                <div key={h.id} className="flex items-center justify-between py-3">
                  <span className="text-sm text-slate-800">{h.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">₹</span>
                    <input type="number" min="0" value={costs[h.id] ?? ""} placeholder="0" data-testid={`fixed-${h.id}`}
                      onChange={(e) => setCosts({ ...costs, [h.id]: e.target.value === "" ? undefined : Number(e.target.value) })}
                      className="qty-input num w-36 h-10 rounded-md border border-slate-200 bg-amber-50/40 px-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
        <div className="rounded-xl border border-rose-200 bg-rose-50/70 p-6 shadow-sm self-start">
          <div className="text-xs font-semibold uppercase tracking-wider text-rose-800/70">Total fixed costs</div>
          <div className="num mt-3 text-3xl font-bold text-rose-900" data-testid="fixed-total">{rs(total)}</div>
          <div className="mt-4 text-sm text-rose-900/70">≈ <span className="num">{rs(total / daysInMonth(month))}</span> per day · <span className="num">{rs(total / 2)}</span> per fortnight</div>
        </div>
      </div>
    </div>
  );
}
